import {
  ExecutionStatus,
  PipettingParameterFieldKey,
  ProtocolEditorViewMode,
  RuntimeMode,
  TaskType,
  WellCount,
} from 'constants/dataEnum';
import {find} from 'lib/objectHelper';
import {getWordBreakClassName} from 'lib/stringHelper';
import _ from 'lodash';

const getWellGroupName = (row, column, rowTipCount, columnTipCount, labwareRowCount, labwareColumnCount) => {
  const is384 = labwareRowCount * labwareColumnCount === WellCount.WELL_384;

  let normalizeRowTipCount = is384 ? rowTipCount * 2 - 1 : rowTipCount;
  let normalizeColumnTipCount = is384 ? columnTipCount * 2 - 1 : columnTipCount;

  if (normalizeRowTipCount >= labwareRowCount && normalizeColumnTipCount >= labwareColumnCount) {
    return 'All Well';
  }

  if (normalizeRowTipCount > 1 && normalizeColumnTipCount === 1) {
    return `Col ${column}`;
  }

  if (normalizeColumnTipCount > 1 && normalizeRowTipCount === 1) {
    return `Row ${row}`;
  }

  if (normalizeRowTipCount === 1 && normalizeColumnTipCount === 1) {
    return `${row}${column}`;
  }

  if (labwareRowCount === 1) {
    normalizeRowTipCount = 1;
  }

  if (labwareColumnCount === 1) {
    normalizeColumnTipCount = 1;
  }

  return `${row}${column}:${String.fromCharCode(row.charCodeAt(0) + normalizeRowTipCount - 1)}${column + normalizeColumnTipCount - 1}`;
};

export function getMinTipPositionTaskId(tasks) {
  const sortedTasks = _.sortBy(tasks, [PipettingParameterFieldKey.TIP_POSITION]);
  return sortedTasks[0].ID;
}

export function generateTaskWellsLocation(number, labware, tasks, labwareState) {
  const labwareMap = {};
  if (number !== 1) {
    tasks.forEach((item) => {
      const selectedLabware = find(labwareState, {LabwareID: item.labwareID});

      if (selectedLabware) {
        const labwareName = selectedLabware.name;
        const labwareRowCount = selectedLabware.rowCount;
        const labwareColCount = selectedLabware.colCount;

        if (!(labwareName in labwareMap)) {
          labwareMap[labwareName] = {taskList: [], labwareRowCount, labwareColCount};
        }

        labwareMap[labwareName].taskList.push(item);
      }
    });
  }

  switch (number) {
    case 1:
      return (
        <>
          &nbsp;
          <span className={`green ${getWordBreakClassName(labware?.name, 19)}`}>{labware?.name}</span>
          &nbsp;
          <span>Well(s)</span>
          &nbsp;
          <span className="green">
            {`[${getWellGroupName(
              tasks[0].row,
              tasks[0].column,
              tasks[0].rowTipCount,
              tasks[0].columnTipCount,
              labware?.rowCount,
              labware?.colCount,
            )}]`}
          </span>
        </>
      );

    case 2:
      return (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <>
          {Object.keys(labwareMap)?.map((name, i) => (
            <>
              {i === 0 ? <>&nbsp;</> : <>,&nbsp;</>}
              <span className={`green ${getWordBreakClassName(name, 19)}`}>
                <b>{name}</b>
              </span>
              &nbsp;
              <span>Well(s)</span>
              &nbsp;
              <span className="green">
                {`[${labwareMap[name].taskList
                  .map((e) =>
                    getWellGroupName(
                      e.row,
                      e.column,
                      e.rowTipCount,
                      e.columnTipCount,
                      labwareMap[name].labwareRowCount,
                      labwareMap[name].labwareColCount,
                    ),
                  )
                  .join('; ')}]`}
              </span>
            </>
          ))}
        </>
      );

    default:
      return (
        <>
          {Object.keys(labwareMap).map((name, i) => (
            <>
              {i === 0 ? <>&nbsp;</> : <>,&nbsp;</>}
              <span className={`green ${getWordBreakClassName(name, 19)}`}>
                <b>{name}</b>
              </span>
              &nbsp;
              <span>Well(s)</span>
              &nbsp;
              <span className="green">
                {`[${labwareMap[name].taskList.map((e) => `${parseFloat(e.volume).toFixed(1)} μL`).join('; ')}]`}
              </span>
            </>
          ))}
        </>
      );
  }
}

export const disallowEditBroadcast = (step, runtimeMode, protocolBroadcastTaskEditStep, protocolEditorMode) => {
  if (runtimeMode) {
    return true;
  }

  if (protocolBroadcastTaskEditStep) {
    return true;
  }

  if (protocolEditorMode !== ProtocolEditorViewMode.BROADCAST) {
    return false;
  }

  return step === 3;
};

export function getBroadcastTaskExecutionStatus(
  number,
  card,
  runtimeMode,
  runtimeStateStatus,
  cardStateTasks,
  runtimeStateCsvRecordID,
) {
  if (!runtimeMode || runtimeStateStatus === RuntimeMode.ABORTED) {
    return ExecutionStatus.NONE;
  }

  const runningTask = cardStateTasks[runtimeStateCsvRecordID];

  if (!runningTask) {
    return ExecutionStatus.NONE;
  }

  const {tasks} = card;

  if (!tasks.includes(runtimeStateCsvRecordID)) {
    return ExecutionStatus.NONE;
  }

  if (runtimeStateStatus === RuntimeMode.FINISH) {
    return ExecutionStatus.EXECUTED;
  }

  switch (runningTask.type) {
    case TaskType.PICK_TIP:
    case TaskType.ASPIRATE:
      return number === 1 ? ExecutionStatus.EXECUTING : ExecutionStatus.NONE;
    case TaskType.DISPENSE:
    case TaskType.EJECT_TIP:
      return number === 2 || number === 3 ? ExecutionStatus.EXECUTING : ExecutionStatus.EXECUTED;
    default:
      return ExecutionStatus.NONE;
  }
}
